import { useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'

import { IGetSubscriptionDetailsResponse } from '../../../../api/subscription'
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner'
import { MailIcon } from '../../../../components/Icons'

interface OverviewTabProps {
  userFirstName?: string
  userLastName?: string
  email: string
  isFetching: boolean
  subscriptionDetails?: IGetSubscriptionDetailsResponse
  onSubmitForm: (data: { firstName: string; lastName: string }) => void
  isUpdating?: boolean
}

type Inputs = {
  firstName: string
  lastName: string
}

export default function OverviewTab({
  userFirstName,
  userLastName,
  email,
  isFetching,
  subscriptionDetails,
  onSubmitForm,
  isUpdating,
}: OverviewTabProps) {
  const nameAbbr = `${userFirstName?.[0] || ''}${userLastName?.[0] || ''}`

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty, isValid },
  } = useForm<Inputs>({
    mode: 'onChange',
    defaultValues: {
      firstName: userFirstName || '',
      lastName: userLastName || '',
    },
  })

  useEffect(() => {
    if (!isUpdating) {
      reset({
        firstName: userFirstName || '',
        lastName: userLastName || '',
      })
    }
  }, [isUpdating])

  useEffect(() => {
    setValue('firstName', userFirstName || '', { shouldValidate: true })
    setValue('lastName', userLastName || '', { shouldValidate: true })
  }, [userFirstName, userLastName, setValue])

  const onSubmit: SubmitHandler<Inputs> = data => {
    onSubmitForm({
      firstName: data.firstName,
      lastName: data.lastName,
    })
  }

  return (
    <>
      <div className="rounded-regular p-6 tc-mv-blur text-white">
        <div className="flex items-center gap-5 flex-wrap">
          <div className="flex gap-4 overflow-hidden">
            <div className="flex-shrink-0 w-[56px] h-[56px] sm:h-[72px] sm:w-[72px] rounded-full tc-mv-blur flex items-center justify-center text-xl sm:text-2xl font-medium">
              {nameAbbr}
            </div>
            <div>
              <h2 className="text-2xl font-medium break-all">
                {isFetching ? (
                  <div className="max-w-64 h-[32px] tc-mv-blur rounded animate-pulse" />
                ) : (
                  `${userFirstName || ''} ${userLastName || ''} `
                )}
              </h2>
              <div className="flex items-baseline gap-2 text-lg mt-3 break-all">
                <div
                  className={` ${isFetching ? 'flex items-center mt-1 ' : ''}`}
                >
                  <MailIcon className="inline me-2" />
                  {isFetching ? (
                    <div className="w-64 h-[22px] tc-mv-blur rounded animate-pulse" />
                  ) : (
                    email
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-4">
            <div>
              <div className="text-[12px] text-[#C1C1CA]">
                Your current plan
              </div>
              <div
                className="text-2xl font-medium"
                data-testid="plan-name-display"
              >
                {isFetching ? (
                  <AnimatedSpinner />
                ) : subscriptionDetails?.billingDetails.paymentType ===
                  'Free' ? (
                  'Unsubscribed'
                ) : (
                  subscriptionDetails?.billingDetails?.planName
                )}
              </div>
            </div>
            <Link to="/pricing" className="btn btn-success flex">
              Upgrade
            </Link>
          </div>
        </div>
      </div>
      <div className="rounded-regular p-6 tc-mv-blur mt-4 text-white">
        <h5>Profile details</h5>
        <form onSubmit={handleSubmit(onSubmit)}>
          <label htmlFor="firstName" className="block mt-4 text-[10px] ">
            First name:
          </label>
          <input
            id="firstName"
            data-testid="first-name-input"
            type="text"
            className="px-4 tc-mv-question-field  h-10 text-white rounded-full w-full mt-2"
            {...register('firstName', {
              required: true,
              validate: value => value.trim().length > 0,
            })}
          />
          <label htmlFor="lastName" className="block mt-4 text-[10px] ">
            Last name:
          </label>
          <input
            id="lastName"
            data-testid="last-name-input"
            type="text"
            className="px-4 tc-mv-question-field  h-10 text-white rounded-full w-full mt-2"
            {...register('lastName', {
              required: true,
              validate: value => value.trim().length > 0,
            })}
          />
          <label htmlFor="email" className="block mt-4 text-[10px] ">
            Email:
          </label>
          <input
            id="email"
            data-testid="email-input"
            type="email"
            className="px-4 tc-mv-question-field  h-10 text-white rounded-full w-full mt-2 opacity-50"
            defaultValue={email}
            disabled
          />
          <div className="mt-6 flex justify-end">
            <button
              disabled={!isValid || isUpdating || !isDirty}
              type="submit"
              className="btn btn-secondary disabled:opacity-60"
            >
              Save changes
              {isUpdating && (
                <span className="ms-2">
                  <AnimatedSpinner />
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}
